import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

const SYSTEM_ADMIN_ID = parseInt(process.env.REACT_APP_SYSTEM_ADMIN_ID || '10');

export const fetchConversations = createAsyncThunk(
  'messaging/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/messages/conversations');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messaging/fetchMessages',
  async ({ otherOrgId, page = 1, perPage = 20 }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/messages?other_org_id=${otherOrgId}&page=${page}&per_page=${perPage}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messaging/sendMessage',
  async ({ receiverOrgId, content, relatedEntityType, relatedEntityId }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/messages', {
        receiver_org_id: receiverOrgId,
        content,
        related_entity_type: relatedEntityType,
        related_entity_id: relatedEntityId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const markMessagesAsRead = createAsyncThunk(
  'messaging/markAsRead',
  async (messageIds, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/messages/mark-read', {
        message_ids: messageIds
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const markConversationAsRead = createAsyncThunk(
  'messaging/markConversationAsRead',
  async (otherOrgId, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/messages/mark-conversation-read', {
        other_org_id: otherOrgId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const checkActiveLoads = createAsyncThunk(
  'messaging/checkActiveLoads',
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/loads/active-with-org/${orgId}`);
      return {
        orgId,
        hasActiveLoads: response.data.hasActiveLoads
      };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchOrganizationsForMessaging = createAsyncThunk(
  'messaging/fetchOrganizationsForMessaging',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/organizations/available-for-messaging');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const selectTotalUnreadCount = (state) => {
  return state.messaging.conversations.reduce((total, conv) => total + (conv.unread_count || 0), 0);
};

const messagingSlice = createSlice({
  name: 'messaging',
  initialState: {
    conversations: [],
    currentConversation: {
      messages: [],
      otherOrgId: null,
      status: 'idle',
      currentPage: 1,
      totalPages: 1,
      totalMessages: 0
    },
    currentUserOrgId: null,
    status: 'idle',
    error: null,
    activeLoads: {},
    availableOrganizations: [],
    availableOrgsStatus: 'idle',
    showNewMessageModal: false
  },
  reducers: {
    addNewMessage: (state, action) => {
        const message = action.payload;
        
        // Add to current conversation if relevant
        if (
          state.currentConversation.otherOrgId === message.sender_org_id ||
          state.currentConversation.otherOrgId === message.receiver_org_id
        ) {
          state.currentConversation.messages.push(message);
        }
        
        // Find the relevant conversation
        const conversationIndex = state.conversations.findIndex(
          (conv) =>
            conv.org_id === message.sender_org_id ||
            conv.org_id === message.receiver_org_id
        );
        
        if (conversationIndex !== -1) {
          // Update existing conversation
          const conversation = state.conversations[conversationIndex];
          state.conversations[conversationIndex] = {
            ...conversation,
            last_message: message,
            unread_count: (
              // Increment unread count if:
              // 1. Message is from the other org (not current user's org) OR
              // 2. Message is from system admin regardless of environment
              (conversation.org_id === message.sender_org_id || message.sender_id === SYSTEM_ADMIN_ID)
                ? conversation.unread_count + 1 
                : conversation.unread_count
            )
          };
        } else {
          // Create new conversation
          const otherOrgId = message.sender_org_id === state.currentUserOrgId 
            ? message.receiver_org_id 
            : message.sender_org_id;
          
          const newConversation = {
            org_id: otherOrgId,
            org_name: message.sender_org_id === state.currentUserOrgId 
              ? message.receiver.org_name 
              : message.sender.org_name,
            last_message: message,
            unread_count: message.sender_org_id === otherOrgId ? 1 : 0
          };
          
          // Add new conversation to the beginning of the list
          state.conversations.unshift(newConversation);
        }
      },
    setCurrentConversation: (state, action) => {
      state.currentConversation.otherOrgId = action.payload;
      state.currentConversation.messages = [];
      state.currentConversation.currentPage = 1;
    },
    updateMessagesReadStatus: (state, action) => {
      const { messages, orgId } = action.payload;
      
      // Update messages in current conversation
      if (state.currentConversation.messages.length > 0) {
        messages.forEach(updatedMessage => {
          const message = state.currentConversation.messages.find(m => m.id === updatedMessage.id);
          if (message) {
            message.read_at = updatedMessage.read_at;
          }
        });
      }

      // Update unread count in conversations list
      const conversation = state.conversations.find(c => c.org_id === orgId);
      if (conversation) {
        conversation.unread_count = Math.max(0, conversation.unread_count - messages.length);
      }
    },
    toggleNewMessageModal: (state, action) => {
      state.showNewMessageModal = action.payload;
    },
    clearAvailableOrganizations: (state) => {
      state.availableOrganizations = [];
      state.availableOrgsStatus = 'idle';
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchConversations
      .addCase(fetchConversations.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle fetchMessages
      .addCase(fetchMessages.pending, (state) => {
        state.currentConversation.status = 'loading';
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.currentConversation.status = 'succeeded';
        // Sort messages by creation date in ascending order (oldest first)
        state.currentConversation.messages = action.payload.messages.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
        state.currentConversation.currentPage = action.payload.current_page;
        state.currentConversation.totalPages = action.payload.pages;
        state.currentConversation.totalMessages = action.payload.total;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.currentConversation.status = 'failed';
        state.error = action.payload;
      })
      // Handle markMessagesAsRead
      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        const { updated_messages } = action.payload;
        const orgId = state.currentConversation.otherOrgId;
        // Update directly instead of dispatching
        const messages = updated_messages;
        
        // Update messages in current conversation
        if (state.currentConversation.messages.length > 0) {
          messages.forEach(updatedMessage => {
            const message = state.currentConversation.messages.find(m => m.id === updatedMessage.id);
            if (message) {
              message.read_at = updatedMessage.read_at;
            }
          });
        }

        // Update unread count in conversations list
        const conversation = state.conversations.find(c => c.org_id === orgId);
        if (conversation) {
          conversation.unread_count = Math.max(0, conversation.unread_count - messages.length);
        }
      })
      // Handle markConversationAsRead
      .addCase(markConversationAsRead.fulfilled, (state, action) => {
        const { updated_messages, org_id } = action.payload;
        
        // Update messages in current conversation
        if (state.currentConversation.messages.length > 0) {
          state.currentConversation.messages = state.currentConversation.messages.map(message => {
            const updatedMessage = updated_messages.find(m => m.id === message.id);
            if (updatedMessage) {
              return { ...message, read_at: updatedMessage.read_at };
            }
            return message;
          });
        }

        // Update unread count in conversations list
        const conversationIndex = state.conversations.findIndex(c => c.org_id === org_id);
        if (conversationIndex !== -1) {
          state.conversations[conversationIndex] = {
            ...state.conversations[conversationIndex],
            unread_count: 0
          };
        }
      })
      
      // Handle checkActiveLoads
      .addCase(checkActiveLoads.fulfilled, (state, action) => {
        state.activeLoads[action.payload.orgId] = action.payload.hasActiveLoads;
      })

      // Handle fetchOrganizationsForMessaging
      .addCase(fetchOrganizationsForMessaging.pending, (state) => {
        state.availableOrgsStatus = 'loading';
      })
      .addCase(fetchOrganizationsForMessaging.fulfilled, (state, action) => {
        state.availableOrgsStatus = 'succeeded';
        state.availableOrganizations = action.payload.organizations;
      })
      .addCase(fetchOrganizationsForMessaging.rejected, (state, action) => {
        state.availableOrgsStatus = 'failed';
        state.error = action.payload;
      });
  }
});

export const { 
  addNewMessage, 
  setCurrentConversation, 
  updateMessagesReadStatus,
  toggleNewMessageModal,
  clearAvailableOrganizations
} = messagingSlice.actions;
export default messagingSlice.reducer; 